import {
  CloudArrowUpIcon,
  DocumentTextIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import React, { useState, useCallback } from 'react';

export interface UploadedFile {
  id: string;
  file: File;
  name: string;
  size: number;
  type: string;
  status: 'uploading' | 'completed' | 'error';
  progress?: number;
  error?: string;
  uploadDate?: string;
  metadata?: Record<string, unknown>;
}

interface FileUploaderProps {
  onFilesUploaded: (files: UploadedFile[]) => void;
  onFileRemoved: (fileId: string) => void;
  acceptedTypes?: string[];
  maxFileSize?: number; // in MB
  maxFiles?: number;
  uploadedFiles?: UploadedFile[];
  isRequired?: boolean;
  label?: string;
  description?: string;
  disabled?: boolean;
  showFileSize?: boolean;
  documentType?: string; // For restricting file types by document category
}

/**
 * FileUploader - Drag & drop file upload component with validation and progress tracking
 * Supports multiple file types, size validation, and upload status management
 */
export const FileUploader: React.FC<FileUploaderProps> = ({
  onFilesUploaded,
  onFileRemoved,
  acceptedTypes = ['.pdf', '.xlsx', '.docx', '.txt', '.md', '.pptx'],
  maxFileSize = 50, // 50MB default
  maxFiles = 10,
  uploadedFiles = [],
  isRequired = false,
  label = 'Upload Documents',
  description = 'Drag and drop files here, or click to browse',
  disabled = false,
  showFileSize = true,
  documentType = '',
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [validationError, setValidationError] = useState<string>('');

  // Determine allowed file types based on document type
  const getAllowedTypes = () => {
    if (documentType === 'meeting_transcript') {
      return ['.docx'];
    }
    return acceptedTypes;
  };

  const allowedTypes = getAllowedTypes();

  const validateFile = (file: File): string | null => {
    // Check file size
    if (file.size > maxFileSize * 1024 * 1024) {
      return `File size must be less than ${maxFileSize}MB`;
    }

    // Check file type
    const fileExtension = `.${file.name.split('.').pop()?.toLowerCase()}`;
    if (!allowedTypes.some(type => type.toLowerCase() === fileExtension)) {
      return `File type not supported. Meeting transcripts require: ${allowedTypes.join(', ')}`;
    }

    // Check max files limit
    if (uploadedFiles.length >= maxFiles) {
      return `Maximum ${maxFiles} files allowed`;
    }

    return null;
  };

  const handleFiles = useCallback((files: FileList) => {
    setValidationError('');
    const validFiles: UploadedFile[] = [];
    const errors: string[] = [];

    Array.from(files).forEach(file => {
      const error = validateFile(file);
      if (error) {
        errors.push(`${file.name}: ${error}`);
      } else {
        const uploadedFile: UploadedFile = {
          id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          file,
          name: file.name,
          size: file.size,
          type: file.type,
          status: 'uploading',
          progress: 0,
        };
        validFiles.push(uploadedFile);
      }
    });

    if (errors.length > 0) {
      setValidationError(errors.join('; '));
    }

    if (validFiles.length > 0) {
      onFilesUploaded(validFiles);
      // Simulate upload progress
      validFiles.forEach(file => {
        simulateUpload(file.id);
      });
    }
  }, [uploadedFiles.length, maxFiles, maxFileSize, acceptedTypes, onFilesUploaded]);

  const simulateUpload = (fileId: string) => {
    // Simulate upload progress for demo purposes
    let progress = 0;
    const interval = setInterval(() => {
      progress += Math.random() * 30;
      if (progress >= 100) {
        clearInterval(interval);
        // Mark as completed - in real app, this would be handled by the parent component
      }
    }, 200);
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled) {
      setIsDragOver(true);
    }
  }, [disabled]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    if (disabled) {return;}

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFiles(files);
    }
  }, [disabled, handleFiles]);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFiles(files);
    }
    // Clear input value to allow re-uploading the same file
    e.target.value = '';
  }, [handleFiles]);

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) {return '0 Bytes';}
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  const getStatusIcon = (status: UploadedFile['status']) => {
    switch (status) {
      case 'uploading':
        return <ArrowPathIcon className="w-4 h-4 text-blue-500 animate-spin" />;
      case 'completed':
        return <CheckCircleIcon className="w-4 h-4 text-green-500" />;
      case 'error':
        return <ExclamationTriangleIcon className="w-4 h-4 text-red-500" />;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-4">
      {/* Upload Area */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <label className="block text-sm font-medium text-gray-700">
            {label}
            {isRequired && <span className="text-red-500 ml-1">*</span>}
          </label>
        </div>
        
        <div
          className={`relative border-2 border-dashed rounded-lg p-6 transition-colors ${
            isDragOver
              ? 'border-[#0097a9] bg-blue-50'
              : disabled
                ? 'border-gray-200 bg-gray-50'
                : 'border-gray-300 hover:border-[#0097a9]'
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <input
            type="file"
            multiple
            accept={acceptedTypes.join(',')}
            onChange={handleFileInput}
            disabled={disabled}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
          />
          
          <div className="text-center">
            <CloudArrowUpIcon 
              className={`mx-auto h-12 w-12 ${
                disabled ? 'text-gray-300' : 'text-gray-400'
              }`} 
            />
            <div className="mt-4">
              <p className={`text-sm ${disabled ? 'text-gray-400' : 'text-gray-600'}`}>
                {description}
              </p>
              <p className={`text-xs mt-1 ${disabled ? 'text-gray-300' : 'text-gray-500'}`}>
                Supported formats: {acceptedTypes.join(', ')} • Max size: {maxFileSize}MB
              </p>
            </div>
          </div>
        </div>

        {validationError && (
          <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
            <ExclamationTriangleIcon className="w-4 h-4 text-red-500 flex-shrink-0" />
            <p className="text-sm text-red-700">{validationError}</p>
          </div>
        )}
      </div>

      {/* Uploaded Files List */}
      {uploadedFiles.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-gray-700">
            Uploaded Files ({uploadedFiles.length}/{maxFiles})
          </h4>
          <div className="space-y-2">
            {uploadedFiles.map((file) => (
              <div
                key={file.id}
                className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg"
              >
                <DocumentTextIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {file.name}
                    </p>
                    <div className="flex items-center gap-2">
                      {getStatusIcon(file.status)}
                      <button
                        onClick={() => onFileRemoved(file.id)}
                        className="text-gray-400 hover:text-red-500 transition-colors"
                        title="Remove file"
                      >
                        <XMarkIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between mt-1">
                    {showFileSize && (
                      <p className="text-xs text-gray-500">
                        {formatFileSize(file.size)}
                      </p>
                    )}
                    {file.status === 'error' && file.error && (
                      <p className="text-xs text-red-600">{file.error}</p>
                    )}
                  </div>

                  {/* Progress bar for uploading files */}
                  {file.status === 'uploading' && (
                    <div className="mt-2">
                      <div className="w-full bg-gray-200 rounded-full h-1.5">
                        <div
                          className="bg-blue-500 h-1.5 rounded-full transition-all duration-300"
                          style={{ width: `${file.progress || 0}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
